(() => {
  if (customElements.get('sticky-add-to-cart')) return;

  const VARIANT_UPDATE_EVENT = 'variant:update';

  /**
   * Sticky Add to Cart component that shows when the main product form
   * scrolls out of view.
   */
  class StickyAddToCart extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    #intersectionObserver = null;

    /** @type {Element | null} */
    #targetElement = null;

    /** @type {Function | null} */
    #resizeHandler = null;

    /** @type {'default' | 'widget' | 'redirect' | 'rent'} */
    #currentPaymentMode = 'default';

    /** @type {{ handle: string, redirectBaseUrl: string, leasingMonths: number, rentRedirectBaseUrl: string, rentMonths: number }} */
    #redirectData = { handle: '', redirectBaseUrl: '', leasingMonths: 36, rentRedirectBaseUrl: '', rentMonths: 0 };

    /** @type {HTMLButtonElement | null} */
    #stickyButton = null;

    connectedCallback() {
      this.#findTargetElement();
      this.#setupIntersectionObserver();
      this.#setupVariantUpdateListener();
      this.#setupResizeObserver();
      this.#setupPaymentModeListener();
    }

    disconnectedCallback() {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
      document.removeEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
      window.removeEventListener('payment:mode-change', this.#onPaymentModeChange);
      if (this.#stickyButton) {
        this.#stickyButton.removeEventListener('click', this.#onStickyButtonClick);
      }
      // Clean up body padding on disconnect
      this.#removeBodyPadding();
      // Clean up resize handler
      if (this.#resizeHandler) {
        window.removeEventListener('resize', this.#resizeHandler);
        this.#resizeHandler = null;
      }
    }

    /**
     * Handles variant update event from the main product form
     * @param {CustomEvent} event
     */
    #onVariantUpdate = (event) => {
      const { resource: variant, data } = event.detail;
      
      if (!variant || !data) return;

      // Update variant ID in form
      /** @type {HTMLInputElement | null} */
      const variantInput = this.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update variant title/name
      /** @type {HTMLElement | null} */
      const variantTitle = this.querySelector('.sticky-add-to-cart__variant');
      if (variantTitle && variant.title) {
        variantTitle.textContent = variant.title;
      }

      // Update price from the new HTML
      const newPrice = data.html?.querySelector('product-price [ref="priceContainer"]');
      const currentPrice = this.querySelector('product-price [ref="priceContainer"]');
      if (newPrice && currentPrice) {
        // Clone the new price to avoid modifying the original
        const priceClone = newPrice.cloneNode(true);
        // Remove monthly payment and tax info from sticky bar
        const additionalInfo = priceClone.querySelector('.price-additional-info');
        if (additionalInfo) {
          additionalInfo.remove();
        }
        currentPrice.innerHTML = priceClone.innerHTML;
      }

      // Update variant image if available
      if (variant.featured_media?.preview_image?.src) {
        /** @type {HTMLImageElement | null} */
        const image = this.querySelector('.sticky-add-to-cart__product-image');
        if (image) {
          const newSrc = variant.featured_media.preview_image.src.replace(/&width=\d+/, '') + '&width=216';
          image.src = newSrc;
          image.srcset = '';
        }
      }

      // Update button state based on variant availability
      /** @type {HTMLButtonElement | null} */
      const addToCartButton = this.querySelector('button[name="add"]');
      if (addToCartButton) {
        const textContent = addToCartButton.querySelector('.add-to-cart-text__content');
        
        if (variant.available) {
          addToCartButton.disabled = false;
          if (textContent) {
            textContent.textContent = this.dataset.addToCartText || 'Add to cart';
          }
        } else {
          addToCartButton.disabled = true;
          if (textContent) {
            textContent.textContent = this.dataset.soldOutText || 'Sold out';
          }
        }
      }
    };

    /**
     * Sets up listener for variant update events
     */
    #setupVariantUpdateListener() {
      document.addEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
    }

    /**
     * Sets up listener for payment mode changes and intercepts sticky button click.
     * When mode is "redirect" (Leasing) or "rent", the sticky button redirects
     * instead of adding to cart.
     */
    #setupPaymentModeListener() {
      window.addEventListener('payment:mode-change', this.#onPaymentModeChange);

      this.#stickyButton = this.querySelector('button[name="add"]');
      if (this.#stickyButton) {
        this.#stickyButton.addEventListener('click', this.#onStickyButtonClick);
      }
    }

    /**
     * Handles payment:mode-change event from payment-mode-selector.
     * Stores current mode and redirect params for use at click time.
     *
     * @param {CustomEvent} event
     */
    #onPaymentModeChange = (event) => {
      const { mode, handle, redirectBaseUrl, leasingMonths, rentRedirectBaseUrl, rentMonths } = event.detail ?? {};
      this.#currentPaymentMode = mode ?? 'default';
      this.#redirectData = {
        handle: handle ?? '',
        redirectBaseUrl: redirectBaseUrl ?? '',
        leasingMonths: leasingMonths ?? 36,
        rentRedirectBaseUrl: rentRedirectBaseUrl ?? '',
        rentMonths: rentMonths ?? 0,
      };
    };

    /**
     * Intercepts the sticky button click in redirect (Leasing) and rent modes.
     *
     * 1. Cancel the default action (form submission) via preventDefault().
     * 2. Undo any AddToCart animation Horizon may have already applied.
     * 3. Redirect with product_handle + selected_variant_id + leasing_months|rent_months.
     *
     * @param {MouseEvent} event
     */
    #onStickyButtonClick = (event) => {
      if (this.#currentPaymentMode !== 'redirect' && this.#currentPaymentMode !== 'rent') return;

      event.preventDefault();

      // Undo Horizon's animation (atc-added class + fly-to-cart element)
      this.#stickyButton?.classList.remove('atc-added');
      document.querySelectorAll('fly-to-cart').forEach((el) => el.remove());

      // Read the current variant ID from the sticky form input (kept in sync by onVariantUpdate)
      const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
      const variantId = variantInput?.value ?? '';

      if (this.#currentPaymentMode === 'rent') {
        const rentBase = this.#redirectData.rentRedirectBaseUrl || window.location.pathname;
        const rentSep = rentBase.includes('?') ? '&' : '?';
        const rentParams = [
          'product_handle=' + encodeURIComponent(this.#redirectData.handle),
          'selected_variant_id=' + encodeURIComponent(variantId),
          'rent_months=' + encodeURIComponent(this.#redirectData.rentMonths),
        ].join('&');
        window.location.href = rentBase + rentSep + rentParams;
        return;
      }

      // redirect (Leasing) mode
      const baseUrl = this.#redirectData.redirectBaseUrl || window.location.pathname;
      const separator = baseUrl.includes('?') ? '&' : '?';
      const params = [
        'product_handle=' + encodeURIComponent(this.#redirectData.handle),
        'selected_variant_id=' + encodeURIComponent(variantId),
        'leasing_months=' + encodeURIComponent(this.#redirectData.leasingMonths),
      ].join('&');

      window.location.href = baseUrl + separator + params;
    };

    /**
     * Sets up window resize listener to update padding when sticky bar height changes
     */
    #setupResizeObserver() {
      let resizeTimeout;
      this.#resizeHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Only update padding if sticky bar is visible
          if (this.classList.contains('is-visible')) {
            this.#addBodyPadding();
          }
        }, 100);
      };
      window.addEventListener('resize', this.#resizeHandler);
    }

    /**
     * Finds the main product form button to observe
     */
    #findTargetElement() {
      // Look for the main add-to-cart button in the product form
      const selectors = [
        'buy-buttons-block add-to-cart-component',
        'product-form-component add-to-cart-component',
        '[data-type="add-to-cart-form"] add-to-cart-component',
        '.product-form-buttons add-to-cart-component',
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element && !this.contains(element)) {
          this.#targetElement = element;
          return;
        }
      }

      // Fallback: find any add-to-cart button not in this component
      const allButtons = document.querySelectorAll('add-to-cart-component');
      for (const button of allButtons) {
        if (!this.contains(button)) {
          this.#targetElement = button;
          return;
        }
      }
    }

    /**
     * Sets up the intersection observer to watch when the target scrolls out of view
     */
    #setupIntersectionObserver() {
      if (!this.#targetElement) {
        // If no target found, hide the sticky bar
        return;
      }

      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show sticky bar when target is NOT intersecting (scrolled out of view)
            if (entry.isIntersecting) {
              this.#hide();
            } else {
              this.#show();
            }
          });
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );

      this.#intersectionObserver.observe(this.#targetElement);
    }

    /**
     * Shows the sticky add to cart bar
     */
    #show() {
      this.classList.add('is-visible');
      // Add padding to body to prevent covering footer content
      this.#addBodyPadding();
    }

    /**
     * Hides the sticky add to cart bar
     */
    #hide() {
      this.classList.remove('is-visible');
      // Remove padding from body
      this.#removeBodyPadding();
    }

    /**
     * Adds padding to body equal to the sticky bar height
     */
    #addBodyPadding() {
      // Wait for render to get actual height
      requestAnimationFrame(() => {
        const height = this.offsetHeight;
        if (height > 0) {
          document.body.style.setProperty('--sticky-bar-offset', `${height}px`);
          document.body.style.paddingBottom = `${height}px`;
        }
      });
    }

    /**
     * Removes padding from body
     */
    #removeBodyPadding() {
      document.body.style.removeProperty('--sticky-bar-offset');
      document.body.style.paddingBottom = '';
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
